import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { track } from '@betterment/js-runtime';
import { Box, Icon, Link, Text } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import { Flash, Flex, HR, StackedProgressBar, Tooltip } from '@betterment/style-closet-react';

import { LegacyTransferType } from '../../graphql/schema';
import { urlBuilder } from '../../Shared/urlBuilder';
import { IraContributionYear } from '../data';

const CURRENT_CONTRIBUTIONS_COLOR = 'dataviz.blue.1';
const PROJECTED_CONTRIBUTIONS_COLOR = 'dataviz.blue.3';

const IraContributionTooltipLimitCopy = (iraContribution: IraContributionYear): string => {
  if (iraContribution.taxYear.isEligibleForIraContributionCatchUp) {
    return 'Limit includes maximum standard contribution amount as well as catch-up contributions for individuals 50 and over.';
  }
  return 'Limit includes maximum standard contribution amount.';
};

const totalProjectedAmount = (iraContribution: IraContributionYear): number => {
  return iraContribution.result.projection?.total?.cents || 0;
};

const projectedFutureContributionAmount = (iraContribution: IraContributionYear): number => {
  return totalProjectedAmount(iraContribution) - iraContribution.result.amount.cents;
};

const progressBarData = (iraContribution: IraContributionYear) => {
  const currentContributionPercent =
    (iraContribution.result.amount.cents / iraContribution.taxYear.iraContributionLimit.cents) * 100;
  const projectedContributionPercent =
    (projectedFutureContributionAmount(iraContribution) / iraContribution.taxYear.iraContributionLimit.cents) * 100;
  return [
    {
      percent: currentContributionPercent,
      indicatorColor: token.var(`colors.${CURRENT_CONTRIBUTIONS_COLOR}`),
    },
    {
      percent: projectedContributionPercent,
      indicatorColor: token.var(`colors.${PROJECTED_CONTRIBUTIONS_COLOR}`),
    },
  ];
};

const isProjectedToMaxOut = (iraContribution: IraContributionYear): boolean => {
  return totalProjectedAmount(iraContribution) >= iraContribution.taxYear.iraContributionLimit.cents;
};

const isMaxedOut = (iraContribution: IraContributionYear): boolean => {
  return iraContribution.result.amount.cents >= iraContribution.taxYear.iraContributionLimit.cents;
};

const transactionFrequencyToDisplayMap = {
  RecurringTransactionFrequencyWeekly: 'weekly',
  RecurringTransactionFrequencyMonthly: 'monthly',
  RecurringTransactionFrequencyAnnually: 'annually',
  RecurringTransactionFrequencyEveryOtherWeek: 'bi-weekly',
  RecurringTransactionFrequencyTwiceAMonth: 'semi-monthly',
};

const RecurringTransactionsCTA = ({ result }: { result: IraContributionYear['result'] }) => {
  if (!result.projection || result.projection.recurringInflows.length === 0) {
    return (
      <Text
        css={{
          fontSize: 'fontSize.50',
        }}
      >
        <Link
          href={urlBuilder.appDepositSetup.absolute()}
          onClick={() => {
            track('ElementClicked', {
              name: 'RecurringTransactionPrompt',
              module: 'IraMatchDetails',
              flow: 'IraDetailsApp',
            });
          }}
        >
          Set up a recurring transaction
        </Link>
      </Text>
    );
  }
  if (result.projection.recurringInflows.length === 1) {
    const recurringInflow = result.projection.recurringInflows[0];
    let transferType;
    if (recurringInflow.legacyTransferType === LegacyTransferType.Deposit) {
      transferType = 'deposit';
    } else {
      transferType = 'transfer';
    }
    return (
      <Box>
        <Text
          color="text.weak"
          css={{
            fontSize: 'fontSize.50',
          }}
        >
          Based on your {formatMoney(recurringInflow.amount)}{' '}
          {transactionFrequencyToDisplayMap[recurringInflow.frequency.__typename]} {transferType}
        </Text>
      </Box>
    );
  }
  return (
    <Text
      color="text.weak"
      css={{
        fontSize: 'fontSize.50',
      }}
    >
      Based on your total recurring transactions
    </Text>
  );
};

interface Props {
  iraContributionCurrentYear: IraContributionYear;
}

const CurrentYearContributions: React.FC<React.PropsWithChildren<Props>> = ({ iraContributionCurrentYear }) => {
  if (!iraContributionCurrentYear) {
    return null;
  }

  return (
    <>
      <Box
        css={{
          marginBottom: 'space.100',
          marginTop: 'space.200',
        }}
      >
        <StackedProgressBar percents={progressBarData(iraContributionCurrentYear)} />
        <Flex justifyContent="flex-end" mt="1">
          <Flex>
            <Text
              color="text.weak"
              css={{
                fontSize: 'fontSize.50',
              }}
            >
              Limit: {formatMoney(iraContributionCurrentYear.taxYear.iraContributionLimit)}
            </Text>
            <Tooltip label={IraContributionTooltipLimitCopy(iraContributionCurrentYear)}>
              <Icon name="InfoCircle" size="small" color="icon.weak" />
            </Tooltip>
          </Flex>
        </Flex>
      </Box>
      <Flex justifyContent="space-between" my="6">
        <Flex>
          <Icon name="Bullet" size="small" color={CURRENT_CONTRIBUTIONS_COLOR} />
          <Text>Your contributions</Text>
        </Flex>
        <Text>{formatMoney(iraContributionCurrentYear.result.amount)}</Text>
      </Flex>
      <HR />
      <Flex justifyContent="space-between" my="6">
        <Box>
          <Flex>
            <Icon name="Bullet" size="small" color={PROJECTED_CONTRIBUTIONS_COLOR} />
            <Text>Projected contributions</Text>
          </Flex>
          <Box data-testid="recurringTransactionsCTA">{RecurringTransactionsCTA(iraContributionCurrentYear)}</Box>
        </Box>
        <Text>{formatMoney(toMoney(projectedFutureContributionAmount(iraContributionCurrentYear)))}</Text>
      </Flex>
      <HR />
      <Flex justifyContent="space-between" my="6">
        <Text>Total contributions</Text>
        <Text>{formatMoney(toMoney(totalProjectedAmount(iraContributionCurrentYear)))}</Text>
      </Flex>
      {isProjectedToMaxOut(iraContributionCurrentYear) && !isMaxedOut(iraContributionCurrentYear) && (
        <Flash variant="success">
          <Text>Congratulations! You’re projected to max out your IRA. Take a second to pat yourself on the back.</Text>
        </Flash>
      )}
    </>
  );
};

export default CurrentYearContributions;
